/**
 * 🏗️ Hook para inicializar os stores de autenticação
 * 
 * Este hook deve ser chamado uma vez no App.tsx para inicializar
 * os stores e configurar listeners de auth.
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useStudentAuthStore } from '@/stores/studentAuthStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { sessionModeStore } from '@/stores/sessionModeStore';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export const useAuthInitialization = () => {
  const initialize = useAuthStore((state) => state.initialize);
  const setSession = useAuthStore((state) => state.setSession);
  const setActiveMode = useAuthStore((state) => state.setActiveMode);
  const availableModes = useAuthStore((state) => state.availableModes);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const initializeStudentAuth = useStudentAuthStore((state) => state.initialize);
  const preferredMode = usePreferencesStore((state) => state.preferredMode);

  useEffect(() => {
    logger.debug('🔵 [useAuthInitialization] Mounting...');
    
    // Initialize both stores ONCE
    initialize();
    initializeStudentAuth();

    // 🔥 CRITICAL: onAuthStateChange should ONLY update session, NOT re-initialize
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('🔐 [Auth Event]:', event, '| isInitialized:', isInitialized);
        
        // ✅ Only synchronous state updates - NO re-initialization!
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setSession(session);
          // Re-initialize to fetch roles/profile for new sign-in
          if (event === 'SIGNED_IN' && session) {
            initialize();
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
        }
        // Do NOT call initialize() here - it causes infinite loops!
      }
    );

    return () => {
      logger.debug('🔵 [useAuthInitialization] Unmounting...');
      // ✅ FASE 0: Verificação defensiva para prevenir crash em navegação rápida
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [initialize, initializeStudentAuth]); // Initialize once

  // Separate effect for restoring preferred mode (respects sessionStorage priority)
  useEffect(() => {
    if (!isInitialized) return;

    // 🔒 Se há modo de sessão (ex: admin em refresh), respeitar e não sobrescrever
    const sessionMode = sessionModeStore.get();
    if (sessionMode && availableModes.includes(sessionMode)) {
      logger.debug('🔒 [Preferences] Session mode active, skipping preferredMode restore:', sessionMode);
      return; // Não restaurar preferredMode se sessionMode está ativo
    }

    // Restaurar preferredMode normalmente (exceto admin por segurança)
    if (preferredMode && preferredMode !== 'admin' && availableModes.includes(preferredMode)) {
      logger.debug('🎨 [Preferences] Restoring preferred mode:', preferredMode);
      setActiveMode(preferredMode);
    }
  }, [isInitialized, preferredMode, availableModes, setActiveMode]);
};
